if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.quantityInput = this.querySelector('#quantity');
      this.addButton = this.querySelector('[name="add"]');
      this.variantIdInput = this.querySelector('.product-variant-id');

      this.isNoCharge = this.hasAttribute('data-no-charge');
      this.isDspSku = this.hasAttribute('data-dsp-sku');
      this.variantInventory = parseInt(this.getAttribute('data-variant-inventory') || '0', 10);
      
      this.setupQuantityControls();
      this.setupOptionButtons();
      this.setupFormSubmit();
      
      this.updateShippingNotice();
      this.updatePriceByQuantity();
      this.initializeCurrentVariant();
    }

    isDspSkuValue(sku) {
      return /-DSP(?:-|\/|$)/i.test(String(sku || ''));
    }
    
    initializeCurrentVariant() {
      const variantSelector = this.querySelector('variant-selects');
      if (!variantSelector) return;
      
      const variantsJson = variantSelector.querySelector('.product-variants-json');
      if (!variantsJson) return;
      
      const data = JSON.parse(variantsJson.textContent);
      const variants = data.variants || data;
      
      const currentVariantId = this.variantIdInput.value;
      this.currentVariant = variants.find(v => v.id == currentVariantId);
      
      // Update the featured image if variant has one (handles page refresh with ?variant=ID)
      if (this.currentVariant) {
        this.updateFeaturedImage(this.currentVariant);
      }
    }

    setupQuantityControls() {
      const minusBtn = this.querySelector('.quantity-minus');
      const plusBtn = this.querySelector('.quantity-plus');
      
      if (minusBtn) {
        minusBtn.addEventListener('click', () => {
          const currentValue = parseInt(this.quantityInput.value) || 1;
          if (currentValue > 1) {
            this.quantityInput.value = currentValue - 1;
            this.updatePriceByQuantity();
          }
        });
      }
      
      if (plusBtn) {
        plusBtn.addEventListener('click', () => {
          const currentValue = parseInt(this.quantityInput.value) || 1;
          const maxValue = parseInt(this.quantityInput.getAttribute('max')) || 99;
          if (currentValue < maxValue) {
            this.quantityInput.value = currentValue + 1;
            this.updatePriceByQuantity();
          }
        });
      }
      
      // Validate input on change
      if (this.quantityInput) {
        this.quantityInput.addEventListener('change', () => {
          const value = parseInt(this.quantityInput.value) || 1;
          const min = parseInt(this.quantityInput.getAttribute('min')) || 1;
          const max = parseInt(this.quantityInput.getAttribute('max')) || 99;
          
          if (value < min) this.quantityInput.value = min;
          if (value > max) this.quantityInput.value = max;
          
          this.updatePriceByQuantity();
        });
        
        // Also listen for input events (while typing)
        this.quantityInput.addEventListener('input', () => {
          this.updatePriceByQuantity();
        });
      }
    }

    setupOptionButtons() {
      const optionGroups = this.querySelectorAll('.option-buttons:not(.custom-option-buttons)');
      
      optionGroups.forEach((group, groupIndex) => {
        group.addEventListener('click', (e) => {
          const button = e.target.closest('.option-button');
          
          if (button && !button.disabled) {
            // Remove selected from siblings
            group.querySelectorAll('.option-button').forEach(btn => {
              btn.classList.remove('selected');
            });
            
            // Add selected to clicked button
            button.classList.add('selected');
            
            // Update option availability
            this.updateOptionAvailability();
            
            // Auto-select first available option in subsequent groups (run twice to handle cascading)
            for (let iteration = 0; iteration < 2; iteration++) {
              let selectionChanged = false;
              optionGroups.forEach((nextGroup, nextGroupIndex) => {
                if (nextGroupIndex > groupIndex) {
                  const selectedBtn = nextGroup.querySelector('.option-button.selected');
                  // If current selection is now hidden or no selection exists, auto-select first available
                  if ((selectedBtn && selectedBtn.style.display === 'none') || !selectedBtn) {
                    if (selectedBtn) {
                      selectedBtn.classList.remove('selected');
                    }
                    // Find first visible button
                    const allButtons = nextGroup.querySelectorAll('.option-button');
                    const firstAvailable = Array.from(allButtons).find(btn => btn.style.display !== 'none');
                    if (firstAvailable && !firstAvailable.classList.contains('selected')) {
                      firstAvailable.classList.add('selected');
                      selectionChanged = true;
                    }
                  }
                }
              });
              
              // If we auto-selected something, recalculate availability for next iteration
              if (selectionChanged) {
                this.updateOptionAvailability();
              } else {
                // No changes, break early
                break;
              }
            }
            
            // Update variant
            this.updateVariant();
            
            // Update shipping notice
            this.updateShippingNotice();
          }
        });
      });
      
      // Initialize option availability on load
      this.updateOptionAvailability();
    }
    
    updateOptionAvailability() {
      const variantSelector = this.querySelector('variant-selects');
      if (!variantSelector) return;
      
      const variantsJson = variantSelector.querySelector('.product-variants-json');
      if (!variantsJson) return;
      
      const data = JSON.parse(variantsJson.textContent);
      const variants = data.variants || data; // Support both new and old format
      const optionGroups = variantSelector.querySelectorAll('.option-buttons:not(.custom-option-buttons)');
      
      // Get currently selected options
      const selectedOptions = [];
      optionGroups.forEach((group, index) => {
        const selected = group.querySelector('.option-button.selected');
        selectedOptions[index] = selected ? selected.dataset.optionValue : null;
      });
      
      console.log('Checking availability with selected options:', selectedOptions);
      console.log('Total variants:', variants.length);
      console.log('All variants:', variants.map(v => ({
        id: v.id,
        option1: v.option1,
        option2: v.option2,
        option3: v.option3,
        available: v.available,
        inventory: v.inventory_quantity
      })));
      
      // Update each option group
      optionGroups.forEach((group, optionIndex) => {
        const buttons = group.querySelectorAll('.option-button');
        
        buttons.forEach(button => {
          const optionValue = button.dataset.optionValue;
          
          // Check if this option value exists in any variant combination
          const variantExists = variants.some(variant => {
            if (
              this.isNoCharge
              && this.isDspSkuValue(variant.sku)
              && !variant.available
            ) {
              return false;
            }

            // Check if variant matches this option value
            const variantOptionValue = variant[`option${optionIndex + 1}`];
            if (variantOptionValue !== optionValue) return false;
            
            // For option 1 (COLOR), allow if ANY variant exists with this color
            if (optionIndex === 0) {
              return true;
            }
            
            // For option 2 (STYLE), check if variant matches option 1 selection (if any)
            if (optionIndex === 1) {
              if (selectedOptions[0] && variant.option1 !== selectedOptions[0]) {
                return false;
              }
              return true;
            }
            
            // For option 3 (ACRYLIC), check if variant matches option 1 and 2 selections
            if (optionIndex === 2) {
              if (selectedOptions[0] && variant.option1 !== selectedOptions[0]) {
                return false;
              }
              if (selectedOptions[1] && variant.option2 !== selectedOptions[1]) {
                return false;
              }
              return true;
            }
            
            // Fallback for any additional options
            for (let i = 0; i < selectedOptions.length; i++) {
              if (i !== optionIndex && selectedOptions[i]) {
                if (variant[`option${i + 1}`] !== selectedOptions[i]) {
                  return false;
                }
              }
            }
            
            return true;
          });
          
          if (!variantExists) {
            console.log(`Option ${optionIndex + 1}: "${optionValue}" - NO MATCHING VARIANT FOUND with selections:`, selectedOptions);
          }
          
          // Update button state - hide unavailable options
          if (variantExists) {
            button.disabled = false;
            button.classList.remove('unavailable');
            button.style.display = '';
          } else {
            button.style.display = 'none';
          }
        });
      });
    }


    updateVariant() {
      const variantSelector = this.querySelector('variant-selects');
      if (!variantSelector) return;
      
      const variantsJson = variantSelector.querySelector('.product-variants-json');
      if (!variantsJson) return;
      
      const data = JSON.parse(variantsJson.textContent);
      const variants = data.variants || data; // Support both new and old format

      // Get selected options
      const selectedOptions = [];
      const optionGroups = variantSelector.querySelectorAll('.option-buttons:not(.custom-option-buttons)');
      
      optionGroups.forEach(group => {
        const selected = group.querySelector('.option-button.selected');
        if (selected) {
          selectedOptions.push(selected.dataset.optionValue);
        }
      });
      
      // Find matching variant
      const matchingVariant = variants.find(variant => {
        return selectedOptions.every((option, index) => {
          return variant[`option${index + 1}`] === option;
        });
      });

      if (matchingVariant) {
        this.variantIdInput.value = matchingVariant.id;
        this.currentVariant = matchingVariant;

        if (this.isNoCharge) {
          this.variantInventory = matchingVariant.inventory_quantity || 0;
          this.isDspSku = this.isDspSkuValue(matchingVariant.sku);
          this.toggleAttribute('data-dsp-sku', this.isDspSku);
          this.setAttribute('data-variant-inventory', this.variantInventory);
          if (this.isDspSku) {
            if (this.quantityInput) {
              this.quantityInput.setAttribute('max', this.variantInventory);
              const currentQuantity = parseInt(this.quantityInput.value || '1', 10);
              if (this.variantInventory > 0 && currentQuantity > this.variantInventory) {
                this.quantityInput.value = this.variantInventory;
              }
            }
          } else {
            if (this.quantityInput) this.quantityInput.setAttribute('max', '99');
          }
        }

        this.updatePrice(matchingVariant);
        this.updateAvailability(matchingVariant);
        this.updateSku(matchingVariant);
        this.updateFeaturedImage(matchingVariant);
        this.updateURL(matchingVariant.id);
      }
    }
    
    updatePrice(variant) {
      const priceElement = this.querySelector('.price-current');
      if (!priceElement) return;
      
      const costCents = variant.metafields?.custom?.cost_variant || 0;
      if (!costCents || costCents === 0) {
        // Fallback to variant price if no cost_variant metafield
        priceElement.textContent = this.formatMoney(variant.price);
        priceElement.setAttribute('data-base-price-cents', '0');
        return;
      }
      
      // Store base price in cents in data attribute
      priceElement.setAttribute('data-base-price-cents', costCents);
      
      // Calculate total: cost_variant (cents) × quantity
      this.updatePriceByQuantity();
    }
    
    updatePriceByQuantity() {
      const priceElement = this.querySelector('.price-current');
      if (!priceElement) return;
      
      const basePriceCents = parseInt(priceElement.getAttribute('data-base-price-cents') || 0, 10);
      const quantity = parseInt(this.quantityInput?.value || 1, 10);
      const priceNote = this.querySelector('.price-note');

      if (this.isNoCharge) {
        if (this.isDspSku) {
          priceElement.textContent = '$0.00';
          if (priceNote) priceNote.textContent = 'NOT CHARGED to your account';
          return;
        }
        const freeQty = Math.min(quantity, this.variantInventory);
        const chargedQty = Math.max(0, quantity - this.variantInventory);
        const chargedCents = basePriceCents * chargedQty;
        const dollars = Math.floor(chargedCents / 100);
        const cents = chargedCents % 100;
        priceElement.textContent = `$${dollars}.${cents.toString().padStart(2, '0')}`;
        if (priceNote) {
          if (chargedQty > 0) {
            priceNote.textContent = `${freeQty} NOT CHARGED + ${chargedQty} CHARGED`;
          } else {
            priceNote.textContent = 'NOT CHARGED to your account';
          }
        }
        return;
      }

      if (!basePriceCents) return;
      
      const totalCents = basePriceCents * quantity;
      const dollars = Math.floor(totalCents / 100);
      const cents = totalCents % 100;
      
      priceElement.textContent = `$${dollars}.${cents.toString().padStart(2, '0')}`;
    }


    updateAvailability(variant) {
      if (this.addButton) {
        if (variant.available) {
          this.addButton.disabled = false;
          this.addButton.textContent = 'Add to Cart';
        } else {
          this.addButton.disabled = true;
          this.addButton.textContent = 'Sold Out';
  }
      }
      
      // Update quantity note with variant inventory
      this.updateQuantityNote(variant);
    }
    
    updateQuantityNote(variant) {
      const quantityNote = document.getElementById('quantity-note');
      if (!quantityNote) return;
      
      const inventory = variant.inventory_quantity || 0;

      if (this.isNoCharge) {
        if (this.isDspSku) {
          quantityNote.innerHTML = inventory > 0
            ? `<span class="quick-ship-available" data-inventory="${inventory}">${inventory} available in stock</span><br>Maximum quantity limited to available stock. Items are NOT CHARGED.`
            : 'Out of stock.';
        } else {
          quantityNote.innerHTML = `<span class="quick-ship-available" data-inventory="${inventory}">${inventory} available in stock</span><br>Items within stock are NOT CHARGED. Quantities beyond available stock will be CHARGED.`;
        }
        return;
      }

      if (inventory > 0) {
        quantityNote.innerHTML = `<span class="quick-ship-available" data-inventory="${inventory}">${inventory} quick ship available</span><br>Quantities beyond this are made to order and billed to your no-charge account.`;
    } else {
        quantityNote.innerHTML = 'Quantities are made to order and billed to your no-charge account.';
    }
    }

    updateSku(variant) {
      const skuElement = this.querySelector('.product-sku');
      if (skuElement && variant.sku) {
        skuElement.textContent = variant.sku;
      }
    }

    updateFeaturedImage(variant) {
      if (!variant.featured_image) return;
      
      const mainImage = document.querySelector('.product-main-image');
      if (!mainImage) return;
      
      // Update the image src
      const imageUrl = variant.featured_image.src || variant.featured_image;
      mainImage.src = imageUrl;
      mainImage.srcset = imageUrl;
      
      // Update alt text if available
      if (variant.featured_image.alt) {
        mainImage.alt = variant.featured_image.alt;
      }
    }
    
    updateURL(variantId) {
      if (!window.history.replaceState) return;
      
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url.toString());
    }

    updateShippingNotice() {
      const notice = document.getElementById('ship-notice');
      if (!notice) return;

      // No-charge (Display-No Charge) products are always quick ship
      if (this.isNoCharge) {
        notice.classList.remove('made-to-order');
        notice.innerHTML = '<strong>Quick ship:</strong> items typically ship between 2-3 business days and have no cost towards your no-charge account.';
        return;
      }

      // Get the current variant from the form
      const variantSelector = this.querySelector('variant-selects');
      if (!variantSelector) {
        // No variants, check initial quickship inventory from data attribute
        const quickshipInventory = parseInt(notice.dataset.quickshipInventory || '0', 10);
        return; // Already set correctly on page load
      }
      
      const variantsJson = variantSelector.querySelector('.product-variants-json');
      if (!variantsJson) return;
      
      const data = JSON.parse(variantsJson.textContent);
      const variants = data.variants || data; // Support both new and old format
      const currentVariantId = this.variantIdInput.value;
      const currentVariant = variants.find(v => v.id == currentVariantId);
      
      if (!currentVariant) return;

      // Check if variant has quickship-inventory metafield
      const quickshipInventory = currentVariant.metafields?.custom?.quickship_inventory || 0;

      // Update notice based on quickship inventory
      if (quickshipInventory > 0) {
        notice.classList.remove('made-to-order');
        notice.setAttribute('data-quickship-inventory', quickshipInventory);
        notice.innerHTML = '<strong>Quick ship:</strong> items typically ship between 2-3 business days and have no cost towards your no-charge account.';
      } else {
        notice.classList.add('made-to-order');
        notice.setAttribute('data-quickship-inventory', '0');
        notice.innerHTML = '<strong>Made to order product:</strong> This item is not quick ship. Made to order product typically takes 7-10 days to produce upon receiving product from Delta.';
      }
    }

    setupFormSubmit() {
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        
        if (this.addButton.disabled) return;

        const originalText = this.addButton.textContent;
        this.addButton.disabled = true;
        this.addButton.textContent = 'Adding...';

        const variantId = this.variantIdInput.value;
        const quantity = parseInt(this.quantityInput?.value || 1, 10);
        const costVariant = this.currentVariant?.metafields?.custom?.cost_variant || 0;

        if (this.isNoCharge) {
          this.addNoChargeItems(variantId, quantity, costVariant, originalText);
        } else {
          this.addStandardItem(variantId, quantity, costVariant, originalText);
        }
      });
    }

    addStandardItem(variantId, quantity, costVariant, originalText) {
      const body = {
        items: [{
          id: parseInt(variantId),
          quantity: quantity,
          properties: {'Model Number': document.getElementById('model-number-input')?.value || ''}
        }]
      };
      if (costVariant) {
        body.items[0].properties['_cost_variant'] = costVariant.toString();
      }

      this.postToCart(body, originalText);
    }

    addNoChargeItems(variantId, quantity, costVariant, originalText) {
      const freeQty = Math.min(quantity, this.variantInventory);
      const chargedQty = this.isDspSku ? 0 : Math.max(0, quantity - this.variantInventory);

      const items = [];

      if (freeQty > 0) {
        items.push({
          id: parseInt(variantId),
          quantity: freeQty,
          properties: {
            '_cost_variant': '0',
            '_charge_status': 'NOT CHARGED'
          }
        });
      }

      if (chargedQty > 0) {
        items.push({
          id: parseInt(variantId),
          quantity: chargedQty,
          properties: {
            '_cost_variant': costVariant.toString(),
            '_charge_status': 'CHARGED'
          }
        });
      }

      if (items.length === 0) return;

      this.postToCart({ items }, originalText);
    }

    postToCart(body, originalText) {
      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      })
        .then(response => {
          if (!response.ok) {
            return response.json().then(err => {
              throw new Error(err.description || 'Could not add to cart');
            });
          }
          return response.json();
        })
        .then(data => {
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: { data: { source: 'product-form-component' } }
          }));

          this.addButton.textContent = '✓ Added to Cart';
          this.addButton.style.background = '#4CAF50';

          setTimeout(() => {
            this.addButton.disabled = false;
            this.addButton.textContent = originalText;
            this.addButton.style.background = '';
          }, 2000);
        })
        .catch(error => {
          console.error('Error adding to cart:', error);
          this.addButton.textContent = 'Error - Try Again';
          this.addButton.style.background = '#f44336';

          setTimeout(() => {
            this.addButton.disabled = false;
            this.addButton.textContent = originalText;
            this.addButton.style.background = '';
          }, 3000);
        });
    }



    formatMoney(cents) {
      return '$' + (cents / 100).toFixed(2);
    }
  });
}

// Custom element for variant selects wrapper
if (!customElements.get('variant-selects')) {
  customElements.define('variant-selects', class VariantSelects extends HTMLElement {
    constructor() {
      super();
    }
  });
}
